// What the box says about the thing being CREATED — its own briefing, at the top of the box
// (frame 40005492:247336, "Briefing task").
//
// Pouria, 2026-08-17: "we will show due date and things at top like briefing"; 2026-08-18: "other
// btns should be after task like others — as we set due date it should be on top as briefing." So
// the division of labour is: the ICON TOGGLES in the action row (star, clock, task-square, calendar,
// bell) are the CONTROLS, and this top row is the BRIEFING — it reports what has been answered so
// far. A fact therefore appears here only once it HAS a value; with nothing set the row is empty and
// the box looks as it did before the briefing existed. (The fact stays pressable, to change or clear
// what it says, but it is no longer the only way in.)
//
// A fact that is not offered for this kind (a note has no due date) is never built — the same
// omission rule the entity briefing follows.
//
// SHAPE (2026-08-19, reconciled with the frame): a fact is ONE ROW — an icon, then the value. There
// is no "Due" label over the date any more: in this frame the icon IS the label, and the value is
// written the way a person would say it ("Tomorrow, 8am", "30 minutes before", "Important"). The
// rows fall into TWO COLUMNS — the left one is what has been decided about the thing (when, remind,
// important, waiting on), the right one is what has been gathered beside it (checklist, files).
//
// The entity briefing (frame 40007893:378129, an OPEN task/note/event) is a different surface with
// its own labelled Created/In/Related grid and is untouched by this — see entityFacts.js.

import { parseUtc } from '@/lib/dueStatus'
import { scheduleFactText } from '@/lib/scheduledSend'
import { NEW_EMAIL_TITLE, waitingFactText } from '@/lib/emailCreationView'
import { replyPickRows } from './replyPick'
import { countLabel } from './rows'
import { columnMajor } from './columnFlow'
import { IMPORTANT_GLYPH, IMPORTANT_COLOR, WAITING_GLYPH, CHECKLIST_GLYPH, DATE_GLYPH } from '@/lib/icons/creationGlyphs'

/** The facts each kind can show, in the order the frame stacks them. */
const FOR_KIND = {
  tasks: ['due', 'remind', 'important', 'waiting', 'checklist', 'attachments', 'relation'],
  // An event's people are part of its briefing (frame 40008622:256677 — "2 Participents" under
  // "Briefing Event"), and like the checklist and the files they are a COUNT of what has been
  // gathered rather than an attribute that was answered.
  // `repeat` sits straight under the date (busy-my-web#278): frame 10 stacks
  // `calendar-2 · "Jul 1, 12:30 to 1:30 PM"` over `refresh-2 · "Daily"`, because how often it
  // happens is the second thing said about when it is.
  // The zone and the calendar joined with the 2026-09-15 parity audit (busy-my-web#472 C-7 and
  // busy-my-web#474 C-9, frames 40008622:257078 / 40008894:384566 / 40008863:378008): the briefing
  // lists "Aug 28, 12:30 to 1:30 PM / Daily / GMT +2 Tehran" down the left and "2 Participants /
  // Busy calendar" down the right. The zone is a fact about WHEN (it sits straight under the
  // repeat, which is how the frames stack it), the calendar a fact about WHERE the event is filed.
  // MERGED 2026-09-15: the two clusters found this twice and named it `zone` (C-7) and `timezone`
  // (C-9); `timezone` is the one key that survives, with C-7's rule that it is only spoken once
  // there is a WHEN for it to qualify.
  events: ['due', 'repeat', 'timezone', 'remind', 'important', 'participants', 'calendar', 'attachments', 'relation'],
  notes: ['important', 'attachments', 'relation'],
  // Email has a briefing too (section 40008595:224771 — "Briefing Email" over an Important chip, an
  // "N attachments" chip and a waiting-on date). It has no due date and no reminder of its own: an
  // email's one date IS the waiting-on, which is what `dueDate` carries on the email record (the
  // same field EmailForm's waiting-on menu writes).
  // `schedule` is the Gmail-style scheduled send (#158, frame 40008976:417412 — "Send Jul 22, at
  // 12:30 PM" under the Important chip). It reports the chosen DELIVERY time, which is a different
  // date from the waiting-on and so gets its own fact rather than borrowing that one.
  emails: ['important', 'waiting', 'schedule', 'attachments', 'relation'],
  // A contact's briefing reports which of its optional FIELDS have been filled — the frames write
  // the field's NAME, not its value ("Email", "Phone number", "Address"), because the values are
  // already on screen in the rows right below (frame 40005600:286240). `attachments` joined with
  // busy-my-web#240: a contact takes documents like every other kind now, and a file picked while
  // one is being composed has to be reported the same way it is on a task.
  // `job` and `company` joined with busy-my-web#282: frame ct-07 ("add all") reports all four
  // filled fields — "Email / Phone number / Job title | Company" — and the two the action bar's
  // briefcase and building toggles open were the only ones the strip stayed silent about, so a
  // contact could carry a job title that its own briefing never mentioned.
  contacts: ['email', 'phone', 'job', 'company', 'birthday', 'address', 'note', 'website', 'attachments', 'relation'],
}

// The contact fields above: present in the briefing the moment they hold something, and reported by
// name rather than by value.
const CONTACT_FIELDS = new Set(['email', 'phone', 'job', 'company', 'birthday', 'address', 'note', 'website'])

// Reminder is only offered once there is something to remind about — the bell appears only with a
// due date set, and the fact keeps that rule.
const NEEDS_DUE = new Set(['remind'])

/** The name each fact answers to when it is spoken rather than drawn (tooltips, aria). */
const LABELS = {
  due: { tasks: 'Due', events: 'When', default: 'Due' },
  remind: { default: 'Reminder' },
  repeat: { default: 'Repeated' },
  important: { default: 'Important' },
  waiting: { default: 'Waiting on' },
  schedule: { default: 'Scheduled send' },
  checklist: { default: 'Checklist' },
  participants: { default: 'Participants' },
  timezone: { default: 'Time zone' },
  calendar: { default: 'Calendar' },
  attachments: { default: 'Attachments' },
  email: { default: 'Email' },
  phone: { default: 'Phone number' },
  job: { default: 'Job title' },
  company: { default: 'Company' },
  birthday: { default: 'Birthday' },
  address: { default: 'Address' },
  note: { default: 'Note' },
  website: { default: 'Website' },
  relation: { default: 'Related' },
}

// The frame's own glyphs, by name in the default icon pack: calendar-2 for the date, the bell for
// the reminder, the BOLD (filled) star for Important, the clock for Waiting on, the tick-square for
// the checklist and the paperclip for the files.
// …and the four that have a TOOLBAR twin read it from lib/icons/creationGlyphs (busy-my-web#641,
// Pouria 2026-10-16): a fact and the toggle that sets it wear one glyph.
const ICONS = {
  due: DATE_GLYPH,
  // The frame's refresh-2 glyph; `recurring` is that arrow-circle's name in the packs, and it is the
  // one the entity rail's own "Repeated" row already wears (lib/entityTimeline.js).
  repeat: 'recurring',
  remind: 'notification',
  // The frame's star is the BOLD (solid) one, which the pack's outline `important` is not — the two
  // sit side by side in the box (the action row's toggle is the outline star until it is pressed),
  // so the briefing needs the filled glyph of its own.
  // …and it is the SAME filled star the lit toggle wears (#641): Figma's vuesax/bold/star, where the
  // strip used to draw the pack's sharp five-point `important-bold`.
  important: IMPORTANT_GLYPH,
  // The toolbar's own outline clock (#641), not the second clock drawing `waiting-on` was.
  waiting: WAITING_GLYPH,
  // The frame draws the scheduled send with the same timer glyph the "Schedule send" menu item
  // wears, so the menu that sets it and the fact that reports it read as one thing.
  schedule: 'waiting-on',
  // The action row's own task-square (Figma 40008539:201422, Pouria 2026-10-15), not `checked`.
  checklist: CHECKLIST_GLYPH,
  // The frame's 14px profile-2user glyph; `contacts` is that glyph's name in the icon packs.
  participants: 'contacts',
  // The frame's globe for the zone (the same glyph the pane's own zone chip wears) and the
  // calendar-in-a-circle the entity rail's "Calendar" row already uses (40008863:378008,
  // lib/entityTimeline.js); `global` and `calendar` are those glyphs' names in the packs.
  timezone: 'global',
  calendar: 'calendar',
  attachments: 'attachment',
  // The contact fields wear the same glyphs their action-row toggles do.
  email: 'sms',
  phone: 'phone',
  // The briefcase and the building the action bar's own two toggles wear (#282, frame ct-07).
  job: 'briefcase',
  company: 'building',
  birthday: 'calendar',
  address: 'location',
  note: 'notes',
  website: 'link',
  // The pack's own glyph — the globe every Research Pack node in the story rail wears.
  relation: 'global',
}

// Which column each fact sits in — decisions left, what has been gathered right.
const COLUMN = {
  due: 'left', repeat: 'left', timezone: 'left', remind: 'left', important: 'left', waiting: 'left', schedule: 'left',
  checklist: 'right', participants: 'right', calendar: 'right', attachments: 'right',
  // A contact's fields are all one column — the frame stacks them, and the empty right-hand column
  // collapses (the #109 rule), so the strip stays flush left.
  // …with COMPANY the one exception (busy-my-web#282, frame ct-07): the organisation is the fact
  // about where the person is rather than about how to reach them, and the frame draws it in the
  // second column beside the three contact details. It is also what keeps the strip two rows tall
  // instead of four once every field is filled.
  email: 'left', phone: 'left', job: 'left', company: 'right',
  birthday: 'left', address: 'left', note: 'left', website: 'left',
  // Where the thing is being made — gathered context, not a decision, so it joins the right column
  // beside the checklist and the files (busy-my-web#172).
  relation: 'right',
}

// A fact wears the SAME colour its action-row toggle shows once it is ON (Pouria, 2026-08-19: "those
// things we show at top as briefing should be colorized same as btn like important should be
// yellow and so on") — read straight off the toggle buttons' own active classes in SmartBox.vue
// (`quickImportant`/`quickWaiting`/checklist), so the pairing can never drift from what pressing the
// icon actually turns on. Due and Reminder toggle to near-black (`#111827`) when set, which is not a
// colour to repeat here, and Attachments has no toggle of its own — both stay neutral (undefined).
// …and the star is the frame's own #EAB308, the lit toggle's colour, not the #e4aa2c amber it was
// (busy-my-web#641: one glyph, one colour).
const COLOR = {
  important: IMPORTANT_COLOR,
  waiting: '#3741f3',
  // NEAR-BLACK, not indigo (busy-my-web#281): tk-13 draws the checklist chip's task-square in the
  // strip's own #111827, and tk-11 draws the toggle it mirrors the same way once the checklist is
  // open. The pair moved together — see ActionRow.vue's own checklist toggle.
  checklist: '#111827',
}

// Facts that carry the ⊗. A count is not cleared from the briefing (its items and its files are
// removed where they live), so only the answered attributes wear one.
// …and not the two FLAGS (Pouria, 2026-10-14, frame 40008539:200623 "Task creation mode-as
// important"): "★ Important" and "Waiting on" are switched off by the same star / clock toggle in
// the action row that switched them on, so a ⊗ beside them was a second off-switch for a one-click
// flag. The dated answers (due, reminder, schedule) keep theirs.
const REMOVABLE = new Set(['due', 'remind', 'schedule'])
// …while every answered attribute is still a CONTROL: pressing its chip reopens what set it, flags
// included. Kept apart from REMOVABLE so dropping a ⊗ never drops the press.
const CONTROLS = new Set(['due', 'remind', 'important', 'waiting', 'schedule'])
// An email's waiting-on is a DATE, not a flag (#118): no one-click toggle undoes it, so it keeps its ⊗.
// …and NONE of it outside the email composer (Pouria, 2026-10-14, screenshot of "19 Oct, 9am ⊗ /
// 1 day before ⊗": "we won't have x in here as well and also remove the click"): a Task / Note / Event /
// Contact strip only REPORTS what is set — no ⊗, and no press reopening the control. The action row's
// own toggles and the date pane are where those are changed. The email composer keeps both: its
// waiting-on is a date with no one-click toggle to undo it (#118).
// …except a TASK's two dated answers (Figma "Task creation design" frame 14, node 40008539:201434,
// Pouria 2026-10-15 "make sure we implement exactly"): the frame draws a small grey clear dot after
// the date ("Jul 12, 12:30 PM ⦁"), and the phone frame m14 one after the reminder too, so a task's
// due date and reminder are clearable from the strip again — still no press reopening the pane.
const removableFor = (key, kind) => (kind === 'emails' && (REMOVABLE.has(key) || key === 'waiting'))
  || (kind === 'tasks' && (key === 'due' || key === 'remind'))
const controlFor = (key, kind) => (kind === 'emails' && (CONTROLS.has(key) || key === 'checklist' || key === 'repeat') ? key : null)

const label = (key, kind) => LABELS[key][kind] || LABELS[key].default

/** The kind, as the header says it: "Briefing task". */
// EVERY kind is capitalised — "Briefing Email" (40008595:239121), "Briefing Note"
// (40008598:242575), "Briefing Event" (40008622:256677), "Briefing Contact" (40005600:286240) and,
// since the task section was remeasured (busy-my-web#281, tk-01), "Briefing Task". The lowercase
// "task" was the last survivor of the frame this file was first written against; with four sections
// capitalising it the odd one out read as a typo rather than as a distinction.
const KIND_WORD = { tasks: 'Task', events: 'Event', notes: 'Note', emails: 'Email', contacts: 'Contact' }

/**
 * Kinds whose briefing is drawn even with nothing in it — see `creationBriefing`.
 *
 * EVENTS joined with busy-my-web#272: the event-creation frames (40008622:255757, frames 1–9) all
 * draw "Briefing . Event" over the single line "Nothing to brief yet." from the moment the mode
 * opens, exactly as the email section does. Fatemeh's report is the other half of the same rule —
 * the box used to open with a DEFAULT time in that strip, so the briefing reported a decision
 * nobody had made. The strip now opens empty and fills as the user answers.
 *
 * CONTACTS joined with busy-my-web#282, which is the same finding one section on: ct-01 (the mode's
 * default frame, before anything has been typed) draws "Briefing . Contact" over the single
 * "Nothing to brief yet." line, exactly as the email and event sections do.
 *
 * NOTES joined with busy-my-web#283 — nt-01 draws it too.
 *
 * TASKS joined with busy-my-web#281 (tk-01, the mode's default frame): the last section still
 * waiting for a first fact now draws "Briefing . Task" over "Nothing to brief yet." on open, like
 * the other four. The set is therefore every kind, and the rule is simply "a creation mode opens
 * with its briefing" — but it is kept as an explicit set rather than collapsed away, so a kind added
 * later has to state which side of the rule it is on instead of inheriting one silently.
 */
const ALWAYS_SHOWN = new Set(['emails', 'events', 'contacts', 'notes', 'tasks'])

/** The empty briefing's one line, frame 40008595:224783. */
export const NOTHING_TO_BRIEF = 'Nothing to brief yet.'

// The reminder's lead time only ("30 minutes before") — a time phrase read as one, so it keeps its
// lower case; the counts of THINGS go through `countLabel` (busy-my-web#609).
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

const toDate = (v) => {
  if (v === null || v === undefined || v === '') return null
  const d = v instanceof Date ? v : parseUtc(v)
  return d && !isNaN(d.getTime()) ? d : null
}

/**
 * The reminder, said as the frame says it: "30 minutes before" — how far AHEAD of the due date it
 * lands, not a second timestamp. A reminder is always chosen relative to the thing it reminds about,
 * so repeating the date is the one piece of information the reader already has.
 *
 * Returns '' when there is nothing to be relative to (no due date, an unreadable one, or a reminder
 * that is not before it) — the caller then falls back to writing the reminder's own date out.
 */
export function remindLead(remind, due) {
  const at = toDate(remind)
  const on = toDate(due)
  if (!at || !on) return ''
  const ms = on.getTime() - at.getTime()
  if (ms < 0) return ''
  // A reminder AT the due moment is the phone list's "At time of event" (Figma m12).
  if (ms === 0) return 'At time of event'
  const minutes = Math.round(ms / 60000)
  if (minutes < 60) return `${plural(minutes, 'minute')} before`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${plural(hours, 'hour')} before`
  const days = Math.round(hours / 24)
  // Whole weeks read as weeks — the custom reminder's own unit (frames 15–17: Min/Hours/Days/Weeks).
  if (days % 7 === 0) return `${plural(days / 7, 'week')} before`
  return `${plural(days, 'day')} before`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A TASK's due date as the creation briefing writes it (Figma frame 14, node 40008539:201434):
 * "Jul 12, 12:30 PM" — month first, the clock with its minutes and an upper-case meridiem — for any
 * day past tomorrow. Today and tomorrow keep the app's narrated "Tomorrow, 8am" (the phone frame m14
 * writes exactly that), which is what `relative` (the box's own formatter) returns for them.
 */
export function taskDueText(value, relative, now = new Date()) {
  const d = toDate(value)
  // A value this module cannot read is the box's formatter's to spell, as every other date is.
  if (!d) return relative(value)
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (sameDay(d, now) || sameDay(d, tomorrow)) return relative(value)
  const h = d.getHours()
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${h % 12 || 12}:${mm} ${h >= 12 ? 'PM' : 'AM'}`
}

/** Are these two instants on the same calendar day? (Local days — the ones the picker draws.) */
const sameDay = (a, b) => {
  const from = toDate(a)
  const to = toDate(b)
  if (!from || !to) return true
  return from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth() && from.getDate() === to.getDate()
}

/**
 * WHEN, as the briefing says it — the one place the four cases are decided (busy-my-web#278).
 *
 *   all day, one day      "1 Jul 26"                 — there is no clock to report
 *   all day, a span       "1 Jul 26 – 5 Jul 26"
 *   timed, across days    "1 Jul 26 – 5 Jul 26"      — the DAYS are the fact; a bare end clock
 *                                                      ("Jul 1, 12:30 – 1:30 PM") would read as one
 *                                                      afternoon rather than five days
 *   timed, one day        the box's own range/date ladder, exactly as before
 *
 * `day` is the date-pane's own dateless spelling ("1 Jul 26"), passed in like the other formatters
 * so this module stays free of locales.
 */
function whenText(kind, state, { date, range, day }) {
  // A DATE-ONLY task (lib/taskDue.js, Pouria 2026-09-22) reads as its day alone — "23 Sep 26", not
  // "Tomorrow, 12am": the midnight is storage, not a time anyone chose.
  if (kind === 'tasks' && state.allDay) return day(state.due)
  if (kind === 'tasks') return taskDueText(state.due, date)
  if (kind !== 'events') return date(state.due)
  const span = state.dueEnd && !sameDay(state.due, state.dueEnd)
  if (state.allDay) return span ? `${day(state.due)} – ${day(state.dueEnd)}` : day(state.due)
  if (span) return `${day(state.due)} – ${day(state.dueEnd)}`
  return state.dueEnd ? range(state.due, state.dueEnd) : date(state.due)
}

/**
 * The facts to draw for a creation, in order — the ones that have been ANSWERED, and only those.
 *
 * @param kind      'tasks' | 'events' | 'notes' (the box's quick-create mode)
 * @param state     { due, dueEnd, allDay, repeat, remind, important, waiting, checklist,
 *                  participants, attachments, attachmentFiles, attachmentsSettled, relation } — the
 *                  three counts (and, optionally, the attached files' { name, contentType } plus
 *                  whether the attach pane's Save has settled them, which turn the count into
 *                  "1 JPG selected" / "1 JPG attached"), then the focused pack's
 *                  NAME. `allDay` and `repeat` are an event's own two switches (#278): the first
 *                  changes how WHEN is spelled, the second is a fact of its own ("Daily")
 * @param format    { date(value), range(from, to), day(value) } — the box's own formatters, passed in so this
 *                  module stays free of locales; the relative reminder is worked out here because it
 *                  is about the two values' distance rather than about how a date is spelled
 * @returns [{ key, label, value, icon, column, removable, set, control }] — `set` is always true (an
 *          unanswered fact is not returned at all); `control` is what a click reopens, or null for
 *          the counts, which are not controls
 */
export function creationFacts(kind, state = {}, format = {}) {
  const keys = FOR_KIND[kind] || []
  const date = typeof format.date === 'function' ? format.date : (v => String(v ?? ''))
  const range = typeof format.range === 'function' ? format.range : ((a, b) => `${date(a)} – ${date(b)}`)
  // The dateless spelling an all-day or multi-day event is reported in ("1 Jul 26"); falls back to
  // the full one, so a caller that hands over only the two original formatters loses nothing.
  const day = typeof format.day === 'function' ? format.day : date

  return keys
    .filter(key => !(NEEDS_DUE.has(key) && !state.due))
    .map((key) => {
      const fact = {
        key,
        label: label(key, kind),
        icon: ICONS[key],
        // A TASK's "Waiting on" heads the RIGHT column (frame 14: "Waiting on" over "3 Checklists"),
        // superseding its place among the left column's decisions — the frame's own layout wins.
        column: kind === 'tasks' && key === 'waiting' ? 'right' : COLUMN[key],
        removable: removableFor(key, kind),
        // `repeat` is not REMOVABLE — a recurrence is unset by choosing "Does not repeat" in the
        // pane that set it — but it IS a control: pressing the chip reopens that list (#278).
        control: controlFor(key, kind),
        color: COLOR[key] || null,
        set: false,
        value: '',
      }
      if (key === 'due' && state.due) {
        fact.set = true
        fact.value = whenText(kind, state, { date, range, day })
      } else if (key === 'repeat' && state.repeat) {
        // "Daily" (frame 9's chip). The WORD is worked out by lib/eventRecurrence.js and handed in,
        // so the menu row, this chip and the payload cannot spell the same choice three ways.
        fact.set = true
        fact.value = String(state.repeat)
      } else if (key === 'remind' && state.remind) {
        fact.set = true
        fact.value = remindLead(state.remind, state.due) || date(state.remind)
      } else if (CONTACT_FIELDS.has(key)) {
        // Reported by NAME, and only once the field holds something. Not removable: the field is
        // right there in the box, which is where it is cleared.
        if (state[key]) {
          fact.set = true
          // …and the phones are COUNTED once there is more than one (2026-09-15 parity audit C-10,
          // frame 40008936:402311 "2 Phone numbers"): a number passed for `phone` is how many rows
          // hold a number; `true` (or 1) keeps the frame's singular name.
          fact.value = key === 'phone' && typeof state.phone === 'number' && state.phone > 1
            ? `${state.phone} Phone numbers`
            : label(key, kind)
        }
      } else if (key === 'timezone' && state.timezone && state.due) {
        // "GMT +2 Tehran" (frames 40008622:257078 / 40008863:378008) — the zone's own spelling,
        // handed in already spelled by the box, so the pane's chip and this fact cannot disagree.
        // Only once there is a WHEN for it to qualify (#472): the creation frames draw "Nothing to
        // brief yet." until a date is picked, and a zone on an undated event qualifies nothing.
        fact.set = true
        fact.value = String(state.timezone)
      } else if (key === 'calendar' && state.calendar) {
        // "Busy calendar" (frame 40008863:378008): the calendar's NAME, then the word.
        fact.set = true
        fact.value = `${String(state.calendar).trim()} calendar`
      } else if (key === 'participants' && state.participants > 0) {
        fact.set = true
        fact.value = countLabel(state.participants, 'Participant')
      } else if (key === 'checklist' && state.checklist > 0) {
        fact.set = true
        // "3 Checklists" (busy-my-web#281, tk-13) — the strip's chip names the THING, not the
        // thing's unit; "3 Checklist items" was the entity briefing's longer spelling borrowed into
        // a chip that has half its width.
        fact.value = countLabel(state.checklist, 'Checklist')
      } else if (key === 'relation' && state.relation) {
        // Creating from inside a focused Research Pack (MVP Stage 9). This used to be a line of its
        // own in the box's BODY — "Relationship — Prada Sunglasses" — which reported the same kind
        // of thing the strip above it reports, in a different place and a different type size. It
        // is a fact, so it lives with the facts (busy-my-web#172); the pack's NAME is the whole
        // value, exactly as the entity briefing's own "Related" fact writes it.
        fact.set = true
        fact.value = String(state.relation)
      } else if (key === 'attachments' && state.attachments > 0) {
        fact.set = true
        // When the caller hands over the files themselves (`attachmentFiles`, the creation box), the
        // fact names their TYPE — "1 JPG selected" while the pick is in hand, "1 JPG attached" once
        // `attachmentsSettled` (the attach pane's Save), "3 files …" for a mix (busy-my-web#585,
        // lib/attachedSummary.js). Without them it is the count it always was, capitalised to match
        // the chip beside it (busy-my-web#281, tk-13: "6 Attachments" sits next to "3 Checklists").
        // …and on a Task / Note / Event / Contact the count again (Figma 40013943:443733, Pouria
        // 2026-10-14): the Work-board now says which files are attached and which are only ready, so
        // the strip just counts the attached ones — "2 Attachments" beside the paperclip, as the frame
        // draws it. The email composer keeps #585's typed "1 JPG selected / attached".
        // SUPERSEDED for the email composer too (busy-my-web#650, Figma frame "attachments" of
        // 40008595:224771): the strip reads "6 Attachments" beside the paperclip, like every other
        // kind — #585's typed "1 JPG selected / attached" is gone from the email strip as well, since
        // the Work-board beside it now lists which files are attached. `attachedSummary` stays for
        // its other readers.
        fact.value = countLabel(state.attachments, 'Attachment')
      } else if (key === 'waiting' && kind === 'emails' && state.waiting) {
        // An email's waiting-on carries a DATE, so the fact writes the date out rather than
        // repeating its own name. SUPERSEDED (busy-my-web#650, frames "waiting on 2–4" of
        // 40008595:224771): the frame spells it "Oct 21" / "Jul 22, at 12:30 PM" beside the FILLED
        // indigo clock (vuesax/bold/clock), not the box's relative date beside a near-black outline.
        fact.set = true
        fact.value = waitingFactText(state.waiting) || date(state.waiting)
        fact.icon = 'clock-bold'
      } else if (key === 'schedule' && state.schedule) {
        // "Send Jul 22, at 12:30 PM" (frame 40008976:417412). Written by the schedule module rather
        // than by this file's date formatter so the strip, the progress line and the notification
        // can never spell the same moment three different ways.
        fact.set = true
        fact.value = scheduleFactText(state.schedule)
        fact.color = null
        // The plain outline clock (frames "schedule send 3–5"), not the timer glyph.
        fact.icon = WAITING_GLYPH
      } else if ((key === 'important' && state.important) || (key === 'waiting' && state.waiting)) {
        // The frame writes the flag's own name as the value — the icon and the word are the whole
        // fact ("★ Important"), where a labelled "Important / Yes" said the same thing twice.
        fact.set = true
        fact.value = label(key, kind)
      }
      return fact
    })
    // A briefing reports; it does not ask. An unanswered fact leaves no row — the icon toggle in the
    // action row is where it is answered, and the fact appears the moment it has something to say.
    .filter(fact => fact.set)
}

/**
 * The whole strip the box draws: the frame's header ("Briefing task", receipt glyph) and the facts
 * split into their two columns — or null while nothing has been answered, which is what keeps a
 * fresh box's strip out of the layout entirely.
 *
 * Same arguments as `creationFacts`.
 */
export function creationBriefing(kind, state = {}, format = {}) {
  const facts = creationFacts(kind, state, format)
  const word = KIND_WORD[kind]

  // WHICH conversation this reply is being written on, when the Brain chose it (busy-main-bff#25).
  //
  // Asking "reply to the supplier" inside an Ongoing drafts on a thread the user never pointed at
  // and then opens the composer on it — and the composer is a CREATION, so this strip is the one on
  // screen from that moment on. Without these rows the pick was explained only on the screen the
  // gesture had just left, which meant "Switch thread" — the sole way to correct a wrong pick —
  // could never be reached. Same two rows the story's briefing builds, from the same module, so the
  // wording and the action name cannot drift.
  //
  // ABOVE the facts, not among them: this is what the reply IS, where the facts below are the
  // attributes being set on it. Its own key rather than a left-column fact, because a fact is
  // pressable through `openQuickFact` and "Switch thread" is not one of those controls.
  //
  // Emails only, and only while the caller says a pick is live — the caller owns that condition
  // (SmartBox: composing on the thread the draft names), so leaving the composer, cancelling or
  // sending takes the rows with it without this module knowing anything about routes.
  const pick = kind === 'emails' ? replyPickRows(state.replyPick) : []
  // ADDED only when there is one, so a briefing without a pick is byte-for-byte the value it was.
  const withPick = rest => (pick.length ? { ...rest, replyPick: pick } : rest)

  // ALWAYS-ON kinds (#158): Fatemeh, 2026-08-21 — "the Briefing section shows in ALL states, and if
  // the user has not added anything yet it shows its EMPTY design" (frame 40008595:224783, which
  // draws the header over the single line "Nothing to brief yet."). Email is the only kind whose
  // updated frames have landed; her note says the other creation modes are to follow, so the rest
  // keep the old rule — no facts, no strip — until their own designs arrive.
  if (!facts.length) {
    if (!ALWAYS_SHOWN.has(kind) && !pick.length) return null
    return withPick({
      title: word ? `Briefing ${word}` : 'Briefing',
      icon: 'receipt-text',
      // A strip stating which thread it is replying on is not empty, whatever else is unanswered:
      // "Nothing to brief yet." over "Replying on Kitchen quote" would contradict the line under it.
      empty: !pick.length,
      left: [],
      right: [],
    })
  }
  return withPick({
    title: word ? `Briefing ${word}` : 'Briefing',
    icon: 'receipt-text',
    empty: false,
    left: facts.filter(f => f.column === 'left'),
    right: facts.filter(f => f.column === 'right'),
  })
}

/**
 * The columns the strip draws the facts in.
 *
 * A TASK flows its facts column-major, four to a column (busy-my-web#641, Pouria 2026-10-16; Figma
 * 40008539:200623): date, reminder, Important, Waiting on down the first, the fifth fact heading
 * the second. That replaces frame 14's split by kind (decisions left, "Waiting on / N Checklists"
 * right), which drew three rows beside two. The facts keep their reading order — `left` then
 * `right` is FOR_KIND's own order for a task.
 * Every other kind keeps its two recorded columns — an event's "date / Daily / zone" beside
 * "Participants / calendar" (40008863:378008), a contact's company on the right (#282), the email
 * composer's decided-vs-gathered split — each drawn only when it has something in it (the #109 rule).
 *
 * @param {string} kind
 * @param {{ left?: object[], right?: object[] }|null} briefing  what `creationBriefing` returned
 * @returns {Array<Array<object>>} the non-empty columns, in order
 */
export function creationFactColumns(kind, briefing) {
  const left = briefing?.left || []
  const right = briefing?.right || []
  if (kind === 'tasks') return columnMajor([...left, ...right])
  // A note's facts stack in ONE column — "★ Important" over "6 Attachments" (Figma 40008598:241165,
  // busy-my-web#651).
  if (kind === 'notes') return [[...left, ...right]].filter(col => col.length)
  // The EMAIL card reads its facts as ONE column (busy-my-web#650, frame "attachments" of
  // 40008595:224771: "★ Important" over "6 Attachments"), superseding the decided-vs-gathered split.
  if (kind === 'emails') return [[...left, ...right]].filter(col => col.length)
  return [left, right].filter(col => col.length)
}

export default creationFacts

/**
 * A NEW item's strip (Pouria, 2026-10-14, settling busy-my-web#628 + #629 from Saffet): while a
 * Task / Note / Event / Contact is being CREATED, the header says it is new — "Briefing New Task" —
 * and the empty strip is the header alone, with no "Nothing to brief yet." under it ("no need to tell
 * the user what they already know as it is a new Task"). This supersedes, for these four kinds, the
 * 2026-08-21 rule that an empty creation strip draws that line (#272/#281/#282/#283).
 *
 * Not touched: EDITING an existing item (it is not new), the email composer and the reply strip
 * (outside the request), and a strip that already has facts (only the header word changes).
 * `newItem: true` tells BriefingBlock to skip the empty line. Pure: same input, same output.
 *
 * @param b        the strip `creationBriefing` built (may be null)
 * @param kind     the creation mode ('tasks' | 'notes' | 'events' | 'contacts' | ...)
 * @param creating true while the box is creating, false while it edits an existing item
 */
export const NEW_ITEM_KINDS = new Set(['tasks', 'notes', 'events', 'contacts'])

// …except a NOTE and a CONTACT, whose own sections (Figma 40008598:241165 / 40002140:82284,
// busy-my-web#651 / #649) write the header in sentence case — "Briefing new note", "Briefing new
// contact" — on desktop and phone alike. Figma wins over #628's capitalisation for these kinds; the
// others keep "Briefing New <Kind>" until their frames say so.
const NEW_ITEM_TITLE = { notes: 'Briefing new note', contacts: 'Briefing new contact' }

export function newItemBriefing(b, kind, creating) {
  // …and a NEW EMAIL (busy-my-web#650, Figma 40008595:224771 frame "default"): "Briefing new email",
  // lower-case as the frame writes it, over no empty line — the header alone until a fact lands.
  if (b && creating && kind === 'emails') return { ...b, title: NEW_EMAIL_TITLE, newItem: true }
  if (!b || !creating || !NEW_ITEM_KINDS.has(kind)) return b
  const word = KIND_WORD[kind]
  return { ...b, title: NEW_ITEM_TITLE[kind] || `Briefing New ${word}`, newItem: true }
}

